import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const services = [
  {
    id: 'interior',
    icon: 'ri-home-heart-line',
    title: 'Interior Painting',
    tagline: 'Clean, detailed finishes for every room.',
    description: 'Transform your living spaces with flawless interior painting. We use premium paints and expert techniques to deliver stunning results for living rooms, bedrooms, kitchens, hallways, and more. Our team prepares every surface meticulously to ensure a finish that looks great and lasts for years.',
    benefits: ['Premium low-VOC paints available', 'Full surface preparation included', 'Furniture and floor protection', 'Clean and tidy worksite daily', 'Colour consultation available'],
    image: 'https://readdy.ai/api/search-image?query=professional%20interior%20painting%20Wellington%20home%20beautiful%20freshly%20painted%20living%20room%20warm%20neutral%20tones%20cream%20walls%20elegant%20furniture%20natural%20light%20premium%20quality%20finish%20clean%20minimalist%20modern&width=800&height=550&seq=svcpg001&orientation=landscape',
  },
  {
    id: 'exterior',
    icon: 'ri-building-2-line',
    title: 'Exterior Painting',
    tagline: 'Protect and beautify your home\'s exterior.',
    description: 'Wellington\'s weather demands a tough, durable exterior finish. We use weather-resistant coatings and professional application techniques to ensure long-lasting protection and stunning curb appeal. From timber weatherboards to modern cladding, we handle all exterior surfaces.',
    benefits: ['Weather-resistant premium coatings', 'Full surface preparation and priming', 'Rot and damage repair included', 'Colour matching and consultation', 'Long-lasting results guaranteed'],
    image: 'https://readdy.ai/api/search-image?query=freshly%20painted%20house%20exterior%20Wellington%20New%20Zealand%20white%20walls%20dark%20trim%20professional%20painting%20beautiful%20curb%20appeal%20sunny%20day%20lush%20garden%20residential%20premium%20quality&width=800&height=550&seq=svcpg002&orientation=landscape',
  },
  {
    id: 'roof',
    icon: 'ri-home-gear-line',
    title: 'Roof Painting &amp; Restoration',
    tagline: 'Extend the life of your roof.',
    description: 'A professionally painted roof not only looks great but adds years of life to your roofing material. We use specialist roof coatings that protect against UV, moisture, and Wellington\'s harsh winds. Our process includes thorough cleaning, priming, and application of high-performance coatings.',
    benefits: ['Specialist roof coatings', 'Moss and lichen treatment', 'Full roof inspection included', 'UV and weather protection', 'Extends roof lifespan significantly'],
    image: 'https://static.readdy.ai/image/38235d25ca124ed59b2cbc0dcd639844/7306ee0fc16362583583ef5d5a835adf.jpeg',
  },
  {
    id: 'spraying',
    icon: 'ri-magic-line',
    title: 'Fine Finish Spraying',
    tagline: 'Flawless, high-end spray finishes.',
    description: 'For the ultimate in smooth, flawless finishes, our fine finish spraying service delivers results that brush and roller simply cannot match. Perfect for kitchen cabinets, doors, furniture, and architectural features. We use professional spray equipment in controlled conditions for a factory-quality result.',
    benefits: ['Factory-quality smooth finish', 'Perfect for cabinets and doors', 'Wide range of sheens available', 'Faster application than brush', 'Exceptional durability'],
    image: 'https://readdy.ai/api/search-image?query=professional%20spray%20painting%20fine%20finish%20kitchen%20cabinets%20smooth%20flawless%20white%20surface%20premium%20quality%20spray%20result%20clean%20modern%20kitchen%20transformation%20stunning&width=800&height=550&seq=svcpg004&orientation=landscape',
  },
  {
    id: 'plastering',
    icon: 'ri-tools-line',
    title: 'Plastering & Gib Stopping',
    tagline: 'Perfect surfaces for premium results.',
    description: 'A great paint job starts with a perfect surface. Our plastering and gib stopping service ensures your walls and ceilings are smooth, level, and ready for a flawless finish. We handle everything from minor repairs and patching to full gib stopping on new builds and renovations.',
    benefits: ['New build and renovation gib stopping', 'Crack and damage repair', 'Smooth level finish guaranteed', 'Sanding and preparation included', 'Ready for premium paint application'],
    image: 'https://static.readdy.ai/image/38235d25ca124ed59b2cbc0dcd639844/0a3713e76211fc52ae4f7b34840b57d7.jpeg',
  },
  {
    id: 'washing',
    icon: 'ri-drop-line',
    title: 'House Washing & Water Blasting',
    tagline: 'Clean exterior surfaces, ready to paint.',
    description: 'Before any exterior painting project, thorough cleaning is essential. We offer professional house washing and water blasting services to remove dirt, mould, mildew, and old paint. We also offer standalone house washing to keep your property looking its best year-round.',
    benefits: ['High-pressure water blasting', 'Soft wash for delicate surfaces', 'Mould and mildew treatment', 'Pre-paint preparation', 'Standalone cleaning service available'],
    image: 'https://static.readdy.ai/image/38235d25ca124ed59b2cbc0dcd639844/4f5447e58722aa9eda3b4295594a228e.jpeg',
  },
];

export default function ServicesPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen">
      <Navbar />

      {/* Page Hero */}
      <section
        className="relative pt-32 pb-20"
        style={{
          backgroundImage: `url('https://readdy.ai/api/search-image?query=professional%20painting%20team%20Wellington%20New%20Zealand%20painters%20at%20work%20exterior%20house%20painting%20skilled%20tradespeople%20uniform%20scaffolding%20sunny%20day%20premium%20quality%20service&width=1440&height=500&seq=svchero001&orientation=landscape')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
        }}
      >
        <div className="absolute inset-0 bg-[#1C1C1C]/80" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 text-center">
          <span className="inline-block text-[#9CA3AF] text-sm font-semibold tracking-widest uppercase mb-4">What We Offer</span>
          <h1 className="text-5xl md:text-6xl font-black text-white mb-5 tracking-tight">Our Services</h1>
          <p className="text-white/70 text-lg max-w-2xl mx-auto">
            Complete painting and decorating solutions for homes and businesses across Wellington & Hutt Valley.
          </p>
        </div>
      </section>

      {/* Services */}
      <main className="bg-white">
        {services.map((service, i) => (
          <section
            key={service.id}
            id={service.id}
            className={`py-20 ${i % 2 === 0 ? 'bg-white' : 'bg-[#F5F5F5]'}`}
          >
            <div className="max-w-7xl mx-auto px-6 md:px-10">
              <div className={`flex flex-col lg:flex-row gap-14 items-center ${i % 2 !== 0 ? 'lg:flex-row-reverse' : ''}`}>
                {/* Image */}
                <div className="w-full lg:w-[50%]">
                  <div className="rounded-2xl overflow-hidden h-[380px]">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                {/* Content */}
                <div className="w-full lg:w-[50%]">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#F0F0F0] mb-5">
                    <i className={`${service.icon} text-[#2D2D2D] text-xl`} />
                  </div>
                  <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-2 tracking-tight">{service.title}</h2>
                  <p className="text-[#2D2D2D] font-semibold mb-4">{service.tagline}</p>
                  <p className="text-gray-600 text-base leading-relaxed mb-7">{service.description}</p>

                  <div className="mb-8">
                    <h4 className="font-bold text-gray-900 mb-4 text-sm uppercase tracking-wider">Key Benefits</h4>
                    <ul className="space-y-2.5">
                      {service.benefits.map((b) => (
                        <li key={b} className="flex items-center gap-3">
                          <div className="w-5 h-5 flex items-center justify-center rounded-full bg-[#2D2D2D] flex-shrink-0">
                            <i className="ri-check-line text-white text-xs" />
                          </div>
                          <span className="text-gray-600 text-sm">{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <a
                    href="/contact"
                    onClick={(e) => { e.preventDefault(); navigate('/contact'); }}
                    className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#2D2D2D] text-white font-bold rounded-full whitespace-nowrap hover:bg-[#1a1a1a] transition-colors cursor-pointer text-sm"
                  >
                    Get a Quote <i className="ri-arrow-right-line" />
                  </a>
                </div>
              </div>
            </div>
          </section>
        ))}
      </main>

      {/* CTA */}
      <section className="py-20 bg-[#1C1C1C] text-center">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <h2 className="text-4xl font-black text-white mb-4 tracking-tight">Not sure which service you need?</h2>
          <p className="text-white/60 text-lg mb-8">Get in touch and we'll advise you on the best solution for your property.</p>
          <a
            href="/contact"
            onClick={(e) => { e.preventDefault(); navigate('/contact'); }}
            className="inline-flex items-center gap-2 px-10 py-4 bg-white text-[#1C1C1C] font-bold rounded-full whitespace-nowrap hover:bg-gray-100 transition-colors cursor-pointer text-base"
          >
            Get a Free Quote <i className="ri-arrow-right-line" />
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
